import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Calendar, FileSpreadsheet, IndianRupee, Users, Filter, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';

// Format month as "April 2026"
const formatMonth = (monthStr) => {
  if (!monthStr) return '-';
  const date = new Date(monthStr + '-01');
  return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const MonthlyReports = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadElapsed, setDownloadElapsed] = useState(0);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [reportData, setReportData] = useState(null);
  const [salaryRates, setSalaryRates] = useState({ sweeper: 0, gardener: 0, security: 0, electrician: 0, supervisor: 0 });
  const [backgroundDownloadTaskId, setBackgroundDownloadTaskId] = useState(null);
  const [backgroundDownloadStatus, setBackgroundDownloadStatus] = useState(null);
  
  const [activeTab, setActiveTab] = useState('reports');
  const [discrepancyData, setDiscrepancyData] = useState([]);
  const [discrepanciesLoading, setDiscrepanciesLoading] = useState(false);
  const [showOnlyDiscrepancies, setShowOnlyDiscrepancies] = useState(false);
  const [totalDiscrepancies, setTotalDiscrepancies] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);
 
  // Auth check only - no data fetching here
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);
 
  // Initial data fetch - runs only once on mount
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    // Set default month to current month
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(defaultMonth);
    
    fetchInitialData(defaultMonth);
  }, [authLoading, user, userType]);
 
  // Data fetch is now manual - triggered by Load button
  const handleLoadReport = () => {
    if (selectedMonth) {
      if (activeTab === 'reports') {
        fetchReportData();
      } else {
        fetchDiscrepancyData();
      }
    }
  };
 
  // Auto-load when month, zone, or active tab changes
  useEffect(() => {
    if (!authLoading && user && userType === 'admin' && selectedMonth && hasFetched.current) {
      if (activeTab === 'reports') {
        fetchReportData();
      } else {
        fetchDiscrepancyData();
      }
    }
  }, [selectedMonth, selectedZone, activeTab]);
 
  // Elapsed time timer
  useEffect(() => {
    let interval;
    if (downloading) {
      interval = setInterval(() => {
        setDownloadElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setDownloadElapsed(0);
    }
    return () => clearInterval(interval);
  }, [downloading]);
 
  const fetchInitialData = async (month) => {
    setLoading(true);
    try {
      const response = await adminAPI.getMonthlyZoneAttendance('all', month || selectedMonth);
      setZones(response.data.available_zones || []);
      setSalaryRates(response.data.salary_rates || { sweeper: 0, gardener: 0, security: 0, electrician: 0, supervisor: 0 });
      setReportData(response.data);
      
      const discResponse = await adminAPI.getAttendanceDiscrepancies('all', month || selectedMonth);
      setDiscrepancyData(discResponse.data.data || []);
      setTotalDiscrepancies(discResponse.data.total_discrepancies || 0);
      setTotalRecords(discResponse.data.total_records || 0);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };
 
  const fetchReportData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getMonthlyZoneAttendance(selectedZone, selectedMonth);
      setReportData(response.data);
      setSalaryRates(response.data.salary_rates || { sweeper: 0, gardener: 0, security: 0, electrician: 0, supervisor: 0 });
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchDiscrepancyData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setDiscrepanciesLoading(true);
    }
    try {
      const response = await adminAPI.getAttendanceDiscrepancies(selectedZone, selectedMonth);
      setDiscrepancyData(response.data.data || []);
      setTotalDiscrepancies(response.data.total_discrepancies || 0);
      setTotalRecords(response.data.total_records || 0);
      if (isManualRefresh) {
        toast.success('Discrepancy data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load discrepancy data');
    } finally {
      setDiscrepanciesLoading(false);
      setRefreshing(false);
    }
  };
 
  const handleRefresh = () => {
    if (activeTab === 'reports') {
      fetchReportData(true);
    } else {
      fetchDiscrepancyData(true);
    }
  };

  const downloadDiscrepancyExcel = () => {
    const dataToDownload = showOnlyDiscrepancies 
      ? discrepancyData.filter(r => r.has_discrepancy)
      : discrepancyData;
      
    if (!dataToDownload.length) {
      toast.error('No data to download');
      return;
    }
    
    // Create CSV content (Excel compatible)
    const headers = [
      'Zone',
      'Division',
      'Park Name',
      'Employee Name',
      'Category',
      'Worker Attendance Calendar (Supervisor Login)',
      'Attendance Calendar (Admin Panel)',
      'Monthly Report Data (Admin Panel)',
      'Remarks'
    ].join(',');
    
    const rows = dataToDownload.map(row => [
      `"${row.zone}"`,
      `"${row.division}"`,
      `"${row.park_name.replace(/"/g, '""')}"`,
      `"${row.employee_name.replace(/"/g, '""')}"`,
      `"${row.category}"`,
      row.supervisor_count,
      row.admin_calendar_count,
      row.monthly_report_count,
      `"${row.remarks.replace(/"/g, '""')}"`
    ].join(',')).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const monthName = formatMonth(selectedMonth);
    link.download = `Attendance_Discrepancies_${monthName.replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Discrepancy report downloaded successfully');
  };

  // Format elapsed time helper
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleDownload = async (downloadZone) => {
    setDownloading(true);
    setDownloadProgress(0);
    setBackgroundDownloadTaskId(null);
    setBackgroundDownloadStatus(null);
    
    // Simulate progress animation since axios doesn't support download progress for large files
    const progressInterval = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until complete
        return prev + Math.random() * 10;
      });
    }, 500);
    
    try {
      const response = await adminAPI.downloadMonthlyZoneAttendance(downloadZone, selectedMonth);
      
      clearInterval(progressInterval);
      setDownloadProgress(100);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename
      const monthName = formatMonth(selectedMonth);
      if (downloadZone === 'all') {
        a.download = `All_Zones_Attendance_${monthName}.xlsx`;
      } else {
        a.download = `Zone_${downloadZone}_Attendance_${monthName}.xlsx`;
      }
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Download error:', error);
      // Offer background download as fallback
      toast.error('Direct download timed out. Starting background download...');
      handleBackgroundDownload(downloadZone);
      return;
    } finally {
      if (!backgroundDownloadTaskId) {
        setDownloading(false);
        setDownloadProgress(0);
      }
    }
  };

  // Background download for monthly zone report (fallback)
  const handleBackgroundDownload = async (downloadZone) => {
    try {
      const response = await adminAPI.startMonthlyZoneReportBackgroundDownload(selectedMonth, downloadZone);
      
      setBackgroundDownloadTaskId(response.data.task_id);
      setBackgroundDownloadStatus('processing');
      toast.info('Background download started. Please wait...');
      
      // Poll for status
      pollBackgroundDownload(response.data.task_id);
    } catch (error) {
      console.error('Background download error:', error);
      toast.error('Failed to start background download');
      setDownloading(false);
    }
  };

  // Poll background download status
  const pollBackgroundDownload = async (taskId) => {
    const checkStatus = async () => {
      try {
        const response = await adminAPI.checkDownloadStatus(taskId);
        setBackgroundDownloadStatus(response.data.status);
        setDownloadProgress(response.data.progress || 0);
        
        if (response.data.status === 'ready') {
          // Download the file
          const fileResponse = await adminAPI.downloadBackgroundFile(taskId);
          const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.data.filename || 'download.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success('Download complete!');
          setDownloading(false);
          setBackgroundDownloadTaskId(null);
          setBackgroundDownloadStatus(null);
          setDownloadProgress(0);
        } else if (response.data.status === 'failed') {
          toast.error(`Download failed: ${response.data.error}`);
          setDownloading(false);
          setBackgroundDownloadTaskId(null);
          setBackgroundDownloadStatus(null);
        } else {
          // Still processing, check again in 2 seconds
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Status check error:', error);
        toast.error('Failed to check download status');
        setDownloading(false);
      }
    };
    
    checkStatus();
  };

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = formatMonth(value);
      options.push({ value, label });
    }
    return options;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Monthly Zone Reports
              </h1>
              <p className="text-sm opacity-90">Download attendance and salary reports by zone</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-monthly-reports-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6 bg-white rounded-t-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex-1 py-4 px-6 text-center font-bold border-b-4 transition-colors ${
              activeTab === 'reports'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/30'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50/50'
            }`}
            data-testid="tab-monthly-reports"
          >
            Monthly Reports
          </button>
          <button
            onClick={() => setActiveTab('discrepancy')}
            className={`flex-1 py-4 px-6 text-center font-bold border-b-4 transition-colors ${
              activeTab === 'discrepancy'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/30'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50/50'
            }`}
            data-testid="tab-attendance-discrepancy"
          >
            Attendance Discrepancy
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Selector */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Month</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0 appearance-none bg-white"
                  data-testid="month-select"
                >
                  {getMonthOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Zone Selector */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Zone</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  value={selectedZone}
                  onChange={(e) => setSelectedZone(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0 appearance-none bg-white"
                  data-testid="zone-filter"
                >
                  <option value="all">All Zones</option>
                  {zones.map(zone => (
                    <option key={zone} value={zone}>Zone {zone}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Checkbox filter for discrepancies */}
            {activeTab === 'discrepancy' && (
              <div className="flex items-center self-end h-12">
                <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={showOnlyDiscrepancies}
                    onChange={(e) => setShowOnlyDiscrepancies(e.target.checked)}
                    className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    data-testid="show-only-discrepancies-checkbox"
                  />
                  Show only discrepancies
                </label>
              </div>
            )}
            
            {/* Download Buttons */}
            <div className="flex gap-2 self-end">
              {activeTab === 'reports' ? (
                <button
                  onClick={() => handleDownload(selectedZone)}
                  disabled={downloading || loading}
                  className="h-12 px-6 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                  data-testid="download-selected-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  Download {selectedZone === 'all' ? 'All Zones' : `Zone ${selectedZone}`}
                </button>
              ) : (
                <button
                  onClick={downloadDiscrepancyExcel}
                  disabled={discrepanciesLoading}
                  className="h-12 px-6 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                  data-testid="download-discrepancies-btn"
                >
                  <Download className="w-5 h-5" />
                  Download Discrepancies
                </button>
              )}
            </div>
          </div>
          
          {/* Download Progress Bar */}
          {downloading && activeTab === 'reports' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="text-sm font-semibold text-blue-800">
                    {backgroundDownloadStatus === 'processing' 
                      ? 'Background processing... This is more reliable for large data'
                      : 'Generating report... Please wait'}
                  </span>
                </div>
                <span className="text-sm text-blue-600">
                  {backgroundDownloadStatus ? `${Math.round(downloadProgress)}%` : formatElapsedTime(downloadElapsed) + ' elapsed'}
                </span>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(downloadProgress, 100)}%` }}
                />
              </div>
              <p className="text-xs text-blue-600 mt-2">
                {backgroundDownloadStatus 
                  ? 'Processing in background. File will download automatically when ready.'
                  : 'Large datasets may take 1-2 minutes. Do not close this page.'}
              </p>
            </div>
          )}
        </div>

        {/* Tab 1 Content: Reports */}
        {activeTab === 'reports' && (
          <>
            {/* Salary Rates Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-800">Current Salary Rates (per day)</h3>
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <span className="text-green-700">Sweeper: <strong>₹{salaryRates.sweeper}</strong></span>
                <span className="text-green-700">Gardener: <strong>₹{salaryRates.gardener}</strong></span>
                <span className="text-green-700">Security: <strong>₹{salaryRates.security}</strong></span>
                <span className="text-green-700">Electrician: <strong>₹{salaryRates.electrician || 0}</strong></span>
                <span className="text-green-700">Supervisor: <strong>₹{salaryRates.supervisor || 0}</strong></span>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Configure salary rates in <Link to="/admin/settings" className="underline font-semibold">Admin Settings</Link>
              </p>
            </div>

            {/* Summary Cards */}
            {reportData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{reportData.total_employees}</div>
                      <div className="text-sm text-slate-500">Total Employees</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <IndianRupee className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{formatCurrency(reportData.total_salary)}</div>
                      <div className="text-sm text-slate-500">Total Salary Payable</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{zones.length}</div>
                      <div className="text-sm text-slate-500">Zones Available</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-slate-50 p-4 border-b">
                <h3 className="font-bold text-lg">Attendance Data Preview</h3>
                <p className="text-sm text-slate-500">Showing first 200 records. Download Excel for full data.</p>
              </div>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-4 text-slate-600">Loading report data...</p>
                </div>
              ) : reportData?.data?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="font-semibold">No attendance data found for this period</p>
                  <p className="text-sm">Try selecting a different month or zone</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3 font-bold">Zone</th>
                        <th className="text-left p-3 font-bold">Division</th>
                        <th className="text-left p-3 font-bold">Park Name</th>
                        <th className="text-left p-3 font-bold">Employee Name</th>
                        <th className="text-left p-3 font-bold">Category</th>
                        <th className="text-center p-3 font-bold">Days Present</th>
                        <th className="text-right p-3 font-bold">Salary Payable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData?.data?.slice(0, 200).map((row, idx) => (
                        <tr key={idx} className="border-t hover:bg-slate-50">
                          <td className="p-3 font-medium">{row.zone}</td>
                          <td className="p-3">{row.division}</td>
                          <td className="p-3">{row.park_name}</td>
                          <td className="p-3">{row.employee_name}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              row.category?.toLowerCase() === 'sweeper' ? 'bg-blue-100 text-blue-700' :
                              row.category?.toLowerCase() === 'gardener' ? 'bg-green-100 text-green-700' :
                              row.category?.toLowerCase() === 'security' ? 'bg-orange-100 text-orange-700' :
                              row.category?.toLowerCase() === 'electrician' ? 'bg-yellow-100 text-yellow-700' :
                              row.category?.toLowerCase() === 'supervisor' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {row.category}
                            </span>
                          </td>
                          <td className="p-3 text-center font-bold">{row.days_present}</td>
                          <td className="p-3 text-right font-bold text-green-600">{formatCurrency(row.salary_payable)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {reportData?.data?.length > 0 && (
                      <tfoot className="bg-slate-100 font-bold">
                        <tr>
                          <td colSpan="5" className="p-3 text-right">TOTAL</td>
                          <td className="p-3 text-center">{reportData.data.reduce((sum, r) => sum + r.days_present, 0)}</td>
                          <td className="p-3 text-right text-green-600">{formatCurrency(reportData.total_salary)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>

            {/* Zone-wise Download Cards */}
            <div className="mt-6">
              <h3 className="font-bold text-lg mb-4">Download by Zone</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {zones.map(zone => (
                  <button
                    key={zone}
                    onClick={() => handleDownload(zone)}
                    disabled={downloading}
                    className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-center"
                    data-testid={`download-zone-${zone}`}
                  >
                    <div className="text-2xl font-bold text-indigo-600 mb-1">Zone {zone}</div>
                    <div className="flex items-center justify-center gap-1 text-sm text-slate-500">
                      <Download className="w-4 h-4" />
                      Download
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Tab 2 Content: Discrepancies */}
        {activeTab === 'discrepancy' && (
          <>
            {/* Discrepancy Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{totalRecords}</div>
                    <div className="text-sm text-slate-500">Total Workers Monitored</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{totalDiscrepancies}</div>
                    <div className="text-sm text-red-700 font-semibold">Attendance Discrepancies Found</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Discrepancy Data Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-slate-50 p-4 border-b">
                <h3 className="font-bold text-lg">Attendance Discrepancy Analysis</h3>
                <p className="text-sm text-slate-500">Comparing Supervisor logs against Admin Calendar and Monthly Reports.</p>
              </div>
              
              {discrepanciesLoading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-4 text-slate-600">Analyzing attendance data...</p>
                </div>
              ) : discrepancyData.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <p className="font-semibold text-lg text-slate-700">Perfect Match!</p>
                  <p className="text-sm">No attendance discrepancies detected for this month/zone.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3 font-bold">Zone</th>
                        <th className="text-left p-3 font-bold">Division</th>
                        <th className="text-left p-3 font-bold">Park Name</th>
                        <th className="text-left p-3 font-bold">Employee Name</th>
                        <th className="text-left p-3 font-bold">Category</th>
                        <th className="text-center p-3 font-bold max-w-[150px]">Worker Attendance Calendar (Supervisor Login)</th>
                        <th className="text-center p-3 font-bold max-w-[150px]">Attendance Calendar (Admin Panel)</th>
                        <th className="text-center p-3 font-bold max-w-[150px]">Monthly Report Data (Admin Panel)</th>
                        <th className="text-left p-3 font-bold">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showOnlyDiscrepancies 
                        ? discrepancyData.filter(r => r.has_discrepancy)
                        : discrepancyData
                      ).map((row, idx) => {
                        const supMismatched = row.supervisor_count !== row.admin_calendar_count;
                        const reportMismatched = row.admin_calendar_count !== row.monthly_report_count;
                        
                        return (
                          <tr key={idx} className={`border-t hover:bg-slate-50 ${row.has_discrepancy ? 'bg-red-50/10' : ''}`}>
                            <td className="p-3 font-medium">{row.zone}</td>
                            <td className="p-3">{row.division}</td>
                            <td className="p-3">{row.park_name}</td>
                            <td className="p-3 font-semibold">{row.employee_name}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                row.category?.toLowerCase() === 'temporary' ? 'bg-purple-100 text-purple-700' :
                                row.category?.toLowerCase() === 'sweeper' ? 'bg-blue-100 text-blue-700' :
                                row.category?.toLowerCase() === 'gardener' ? 'bg-green-100 text-green-700' :
                                row.category?.toLowerCase() === 'security' ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {row.category}
                              </span>
                            </td>
                            <td className={`p-3 text-center font-bold ${supMismatched ? 'text-red-600 bg-red-100/50' : ''}`}>
                              {row.supervisor_count}
                            </td>
                            <td className={`p-3 text-center font-bold ${supMismatched || reportMismatched ? 'text-red-600 bg-red-100/50' : ''}`}>
                              {row.admin_calendar_count}
                            </td>
                            <td className={`p-3 text-center font-bold ${reportMismatched ? 'text-red-600 bg-red-100/50' : ''}`}>
                              {row.monthly_report_count}
                            </td>
                            <td className={`p-3 text-sm font-semibold ${row.has_discrepancy ? 'text-red-600' : 'text-slate-500'}`}>
                              {row.remarks}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlyReports;
